/**
 * The one way to read the coaching dashboard switch, `ConfigKey.NewCoachingDashboard` (#5456).
 *
 * Every screen of the coaching dashboard goes through these two functions, so "on" means the same
 * thing everywhere: only an explicit `true`. Anything else (unset, `false`, a string, a config that
 * failed to load) is off, and off is exactly the screens as they were before #5456.
 *
 * The value is resolved for the SIGNED-IN account. While a coach manages a client that is the
 * coach, and under admin impersonation it is the admin. See design-docs/coaching-dashboard.md,
 * "The flag".
 */

import { ConfigKey } from './config-keys';

/** the two config-service calls the helper needs; `ConfigDbService` satisfies it */
export interface CoachingDashboardFlagSource {
  getConfigValueAsync<T>(key: string): Promise<T | undefined>;
  watchConfigValue<T>(key: string, callback: (newValue: T | undefined, oldValue: T | undefined) => void): () => void;
}

/** true only for an exact `true` */
export function isCoachingDashboardValueOn(value: unknown): boolean {
  return value === true;
}

/**
 * Whether the coaching dashboard is on for the signed-in account. Resolves once the config has
 * loaded. A page awaits this in `ionViewCanEnter`, so its first render is already in the right state.
 */
export async function isCoachingDashboardOnAsync(configDb: CoachingDashboardFlagSource): Promise<boolean> {
  return isCoachingDashboardValueOn(await configDb.getConfigValueAsync<unknown>(ConfigKey.NewCoachingDashboard));
}

/**
 * Calls `onChange` whenever the switch changes after the first read. Returns the function that
 * stops watching; call it when the page is destroyed.
 */
export function watchCoachingDashboard(configDb: CoachingDashboardFlagSource, onChange: (isOn: boolean) => void): () => void {
  return configDb.watchConfigValue<unknown>(ConfigKey.NewCoachingDashboard, value => onChange(isCoachingDashboardValueOn(value)));
}
